import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, type PushDevice } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { formatInstant } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { deviceKind, forgetPushDevice, pushDeviceId } from '../../lib/push';

/**
 * "Your devices" — every device registered to this person for alerts, with a
 * Remove button each (Settings → Notifications).
 *
 * The case it exists for: a deleted home-screen app. Its registration lingers
 * and the push service keeps ACCEPTING alerts for it, so it looks alive — but
 * it never confirms one arriving. Each row says when the device last
 * confirmed an alert (fieldiq-api delivery receipts); "never" on an old row is
 * the tell. Removing this device also unsubscribes it here; removing another
 * just forgets it on the server.
 *
 * Times are in the company's zone (Settings → Regional & Tax), never the
 * phone's.
 */
export default function PushDeviceList() {
  const queryClient = useQueryClient();
  const deviceId = pushDeviceId();
  const zone = useTenantTimeZone();
  const kind = deviceKind();
  const [removing, setRemoving] = useState<string | null>(null);

  const { data: devices, isLoading, isError, refetch } = useQuery({
    queryKey: ['push', 'devices', deviceId],
    queryFn: async () => unwrap<PushDevice[]>((await api.push.devices(deviceId)).data) ?? [],
  });

  const remove = async (d: PushDevice) => {
    setRemoving(d.deviceId);
    try {
      if (d.thisDevice) await forgetPushDevice();
      else await api.push.unregister(d.deviceId);
      toast.success(d.thisDevice ? `Alerts are off for this ${kind}` : `${d.label} removed`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not remove that device');
    } finally {
      setRemoving(null);
      queryClient.invalidateQueries({ queryKey: ['push'] });
    }
  };

  const when = (iso: string | null) => (iso ? formatInstant(iso, zone) ?? '—' : null);

  if (isLoading) {
    return <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 size={14} className="animate-spin" /> Loading your devices…</div>;
  }
  if (isError) {
    return (
      <div role="alert" className="flex items-center justify-between gap-3 text-sm text-red-300">
        <span>Could not load your devices.</span>
        <button onClick={() => refetch()} className="px-3 py-1.5 rounded-lg border border-red-400/40 text-xs font-semibold hover:bg-red-500/10">Retry</button>
      </div>
    );
  }
  if (!devices || devices.length === 0) return null;

  return (
    <div aria-labelledby="push-devices-heading" className="pt-2 border-t border-fieldiq-border space-y-2">
      <h3 id="push-devices-heading" className="text-xs font-semibold text-gray-300">Your devices</h3>
      <ul className="space-y-2">
        {devices.map((d) => {
          const confirmed = when(d.lastConfirmedAt);
          const status = !d.working
            ? 'Not working — alerts go by text instead'
            : confirmed
              ? `Last alert confirmed ${confirmed}`
              : d.lastPushedAt
                ? 'Has never confirmed an alert — remove it if this app was deleted'
                : 'No alerts sent yet';
          return (
            <li key={d.deviceId} className="flex items-center justify-between gap-3 text-sm" data-testid="push-device-row">
              <div className="min-w-0">
                <div className="text-white">
                  {d.label}{d.thisDevice && <span className="text-fieldiq-accent"> · this {kind}</span>}
                </div>
                <div className={`text-xs ${d.working ? 'text-gray-400' : 'text-red-300'}`}>
                  {status} · added {when(d.addedAt) ?? '—'}
                </div>
              </div>
              <button
                onClick={() => remove(d)}
                disabled={removing !== null}
                aria-label={`Remove ${d.label}${d.thisDevice ? ` (this ${kind})` : ''}`}
                className="shrink-0 bg-fieldiq-card border border-fieldiq-border px-2.5 py-1.5 rounded-lg text-xs disabled:opacity-50 flex items-center gap-1"
              >
                {removing === d.deviceId ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Remove
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
